import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";

const LS_KEY_DISMISSED = "bypassgo_push_prompt_dismissed";

/**
 * Discrete, non-intrusive push-notification prompt shown to a visitor
 * once they have an active ticket. Behaviour:
 *  - Appears 6 s after the ticket is rendered (gives the visitor time to
 *    see the number first — no aggressive on-load pop-up).
 *  - Slides up from the bottom on mobile / bottom-right on desktop.
 *  - Disappears as soon as the visitor either authorises or dismisses it.
 *  - Dismissal is remembered in localStorage so we don't nag at every poll.
 *  - Auto-hides if the browser does not support the Notification API,
 *    if the visitor has already granted/denied permission, or if there
 *    is no active ticket.
 */
export default function NotifPermissionPrompt({ ticket }) {
  const supported = typeof window !== "undefined" && "Notification" in window;
  const initialPerm = supported ? Notification.permission : "unsupported";
  const [permission, setPermission] = useState(initialPerm);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  // Mount delay — only after a ticket is displayed
  useEffect(() => {
    if (!ticket || !supported) return;
    if (permission !== "default") return;
    if (localStorage.getItem(LS_KEY_DISMISSED) === "1") return;
    const t = setTimeout(() => setVisible(true), 6000);
    return () => clearTimeout(t);
  }, [ticket, supported, permission]);

  const close = (rememberDismiss) => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      if (rememberDismiss) {
        try { localStorage.setItem(LS_KEY_DISMISSED, "1"); } catch {}
      }
    }, 220);
  };

  const askPermission = async () => {
    try {
      const res = await Notification.requestPermission();
      setPermission(res);
      // success or denial — either way, hide the prompt
      close(res === "granted");
    } catch {
      close(true);
    }
  };

  if (!visible || !supported || permission !== "default") return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="notif-permission-prompt"
      className={`fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-[80] transition-all duration-200 ease-out ${
        closing ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
      }`}
    >
      <div className="rounded-2xl bg-white shadow-[0_10px_30px_rgba(15,23,42,0.14)] border border-border p-4 flex items-start gap-3">
        <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center">
          <Bell size={18} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-black text-sm text-ink leading-tight">
            Soyez prévenu sans rester sur l'écran
          </p>
          <p className="mt-1 text-[12px] text-subtle leading-snug">
            Recevez une notification lorsque votre tour approche.
            Vous pouvez quitter cet écran sans perdre votre ticket.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={askPermission}
              data-testid="notif-permission-enable-btn"
              className="px-3 h-9 rounded-md bg-brand hover:bg-brand-hover text-white text-xs font-semibold"
            >
              Autoriser
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              data-testid="notif-permission-later-btn"
              className="px-3 h-9 rounded-md border border-border text-subtle hover:text-ink text-xs font-medium"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => close(true)}
          aria-label="Fermer"
          className="shrink-0 -mr-1 text-subtle hover:text-ink"
          data-testid="notif-permission-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
